import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useContractContexts } from '@/hooks/api/0760_contract_contexts';
import {
  usePurchaseOrderContractTasks,
  usePurchaseOrderContractTasksInvalidate,
} from '@/hooks/api/0780_purchase_order_contract_tasks';
import { useCreatePurchaseOrderContractTask } from '@/hooks/api/0790_create_purchase_order_contract_tasks';
import { ContractContext } from '@/hooks/api/models';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { scrollTop } from '@/utils';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  contract_context_id: number;
  name: string;
  description?: string;
  due_date?: string;
};

export const PurchaseOrderContractTasksNew = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle(`新規契約申請（費用）タスク作成`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        {
          label: `プロジェクト（費用） - ${_t_!.name}`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders`,
        },
        {
          label: `プロジェクト（費用）詳細`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}`,
        },
      ]),
    []
  );

  const { data: contractContexts } = useContractContexts();

  return (
    <WorkspaceLayout>
      <ScrollTop />

      {contractContexts && <_PurchaseOrderContractTasksNew contractContexts={contractContexts} />}
    </WorkspaceLayout>
  );
};

export const _PurchaseOrderContractTasksNew = ({ contractContexts }: { contractContexts: ContractContext[] }) => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { mutate, data, isPending, error } = useCreatePurchaseOrderContractTask();
  const { data: tasks } = usePurchaseOrderContractTasks({
    identifier: _w_!.identifier,
    purchase_order_id: _po_!.id,
    page: 1,
    per: 1000,
  });
  const [autoSettingName, setAutoSettingName] = useState('');
  const { invalidate } = usePurchaseOrderContractTasksInvalidate(_w_!.identifier);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      contract_context_id: contractContexts[0].id,
    },
  });

  const [contractContextId, name] = watch(['contract_context_id', 'name']);

  // タスク名を自動設定する
  useEffect(() => {
    if (tasks && contractContextId) {
      if (autoSettingName == '' || name == autoSettingName) {
        const taskCount = tasks.purchase_order_contract_tasks.filter(
          (t) => t.contract_context.id == contractContextId
        ).length;
        const contractContextLabel = contractContexts.find((c) => c.id == contractContextId)?.label;

        const _suffix = taskCount == 0 ? '' : ` - ${taskCount + 1}`;
        const _n = `${contractContextLabel} - ${_po_!.name}${_suffix}`;
        setValue('name', _n);
        setAutoSettingName(_n);
      }
    }
  }, [tasks, contractContextId]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('契約申請（費用）タスクの登録が完了しました');
      navigate(
        `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}/purchase_order_contract_tasks/${data.id}`
      );
    }
  }, [data]);

  useEffect(() => {
    if (error) scrollTop();
  }, [error]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12 col-lg-9 mx-auto">
          <div className="card mb-4" id="basic-info">
            <div className="card-header d-flex align-items-center">
              <div className="col-md-8">
                <h5 className="mb-1">新規契約申請（費用）タスク作成</h5>
                <p className="text-xs mb-0">NDAや契約書の申請・管理を行うタスクを作成します。</p>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body pt-0 row">
                <ApiErrorMessages error={error} />

                <div className="form-group">
                  <label>プロジェクト名</label>
                  <p className="form-text text-muted text-sm ms-1">{_po_!.name}</p>
                </div>
                <div className="row">
                  <div className="col-12 col-md-6 pe-0">
                    <FormGroup label="申請タスクタイプ" required={true}>
                      <select className="form-control" required={true} {...register('contract_context_id')}>
                        {contractContexts.map((c, i) => (
                          <option key={`cc-${c.id}`} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </FormGroup>
                  </div>
                </div>

                <FormGroup label="タスク名" required={true}>
                  <input
                    type="text"
                    maxLength={50}
                    className={`form-control ${errors.name && 'is-invalid'}`}
                    placeholder={'タスク名'}
                    {...register('name', {
                      required: '入力して下さい。',
                    })}
                  />
                  {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                </FormGroup>

                <FormGroup label="メモ">
                  <textarea className="form-control" maxLength={2000} rows={5} {...register('description')}></textarea>
                </FormGroup>

                <div className="row">
                  <div className="col-12 col-md-6 pe-0">
                    <FormGroup label="期限日">
                      <Controller
                        control={control}
                        name="due_date"
                        render={({ field: { ref, ...field } }) => (
                          <Flatpickr
                            className={`form-control datepickr`}
                            {...field}
                            onChange={(_, dateString) => {
                              field.onChange(dateString);
                              field.onBlur();
                            }}
                            onBlur={() => {}}
                            options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                          />
                        )}
                      />
                    </FormGroup>
                  </div>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                  戻る
                </Link>
                <button className="btn bg-gradient-dark btn-icon">
                  <i className="fas fa-check-circle"></i>登録する
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
